import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import DataAccessButton from '../../components/sidebar/DataAccessButton';
import useDataAccessConsole from '../../Hooks/useDataAccessConsole';

const POINTS = [
  {
    icon: 'mdi:shield-key-outline',
    title: 'Signed in automatically',
    body: 'The console opens through the same SSO handshake the portal already holds, so there is no second login.',
  },
  {
    icon: 'mdi:open-in-new',
    title: 'Opens in a new tab',
    body: 'Your place in the portal is kept — the console runs alongside it rather than replacing it.',
  },
  {
    icon: 'mdi:account-lock-outline',
    title: 'Scoped to your access',
    body: 'What you can see inside the console is decided there, by the same account you are signed in with here.',
  },
];

/**
 * data access console launcher.
 * the same entry point that sits in the navbar, given a page of its own so it can be
 * reached from the sidebar and explained rather than just presented as a bare button.
 * @author Akhil E M
 * @createdAt 27-08-2026
 * @updates 27-08-2026(Akhil EM),
 * @type component
 */
const DataAccessLanding = () => {
  //IDENTICAL BEHAVIOUR TO THE NAVBAR BUTTON — BOTH SHARE ONE HOOK , SO THE SSO HANDSHAKE
  //CANNOT DRIFT BETWEEN THE TWO PLACES IT IS OFFERED FROM
  const { loading, open } = useDataAccessConsole();

  return (
    <GlobalWrapper title="Data Access">
      <Paper
        elevation={0}
        sx={{
          mt: 1,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Stack
          spacing={2.5}
          alignItems="center"
          sx={{
            px: 3,
            py: { xs: 5, md: 7 },
            textAlign: 'center',
            //TINTED FROM THE CONSOLE'S OWN BRAND , SO THE PAGE READS AS THE PRODUCT IT OPENS
            background: (theme) =>
              `linear-gradient(160deg, ${alpha('#7B4B8E', theme.palette.mode === 'dark' ? 0.22 : 0.08)} 0%, ${alpha(
                '#3399CF',
                theme.palette.mode === 'dark' ? 0.18 : 0.07,
              )} 100%)`,
          }}
        >
          <Box
            sx={{
              width: 62,
              height: 62,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
              background: 'linear-gradient(135deg, #7B4B8E 0%, #1D6FB8 58%, #3399CF 100%)',
              boxShadow: `0 10px 26px ${alpha('#1D6FB8', 0.4)}`,
            }}
          >
            <Icon icon="mdi:database-search-outline" width="30" height="30" />
          </Box>

          <Stack spacing={0.75} alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Data Access Console
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 520 }}>
              Query and export the data you are entitled to. The console is a separate application — this
              opens it, already signed in as you.
            </Typography>
          </Stack>

          {/* THE SAME COMPONENT THE NAVBAR RENDERS , SO THE TWO CANNOT LOOK OR BEHAVE DIFFERENTLY */}
          <Box sx={{ pt: 0.5 }}>
            <DataAccessButton loading={loading} onClick={open} />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          divider={<Box sx={{ borderLeft: { md: '1px solid' }, borderTop: { xs: '1px solid', md: 'none' }, borderColor: 'divider' }} />}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        >
          {POINTS.map((p) => (
            <Stack key={p.title} spacing={1} sx={{ flex: 1, p: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Icon icon={p.icon} width="19" height="19" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {p.title}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {p.body}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>
    </GlobalWrapper>
  );
};

export default DataAccessLanding;
